import { UserRecordsV2Switch } from "@/components/user-record/user-records-v2-switch";
import { MatchingProfileSettings } from "@/components/settings/matching-profile-settings";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import {
  PromptSettingsSchema,
  ReactivationSettingsSchema,
  useFetchSettings,
} from "@/lib/api/admin.ts";
import { PromptSettings } from "@/components/settings/prompt-settings.tsx";
import { ScheduleSettings } from "@/components/settings/schedule-settings-wrapper.tsx";
import { Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentUser, UserRole } from "@/lib/api/user.ts";
import { useState } from "react";
import { GroupSettings } from "@/components/settings/group-settings";
import { ReactivationSettings } from "@/components/settings/reactivation-settings";
import { DevSettings } from "@/components/settings/dev-settings";
import { PostHogEvents, useTrackPage } from "@/lib/posthog-events";
import { useAbility } from "@/lib/casl/ability-context";
import { devOverridesEnabled } from "@/lib/hooks/admin/use-dev-overrides";
import { useMentorProfileEnabled } from "@/lib/hooks/use-mentor-profile";
import { useUserRecordsV2 } from "@/lib/hooks/use-user-records-v2";
import { TrainingTeenSettings } from "@/components/settings/training-teen-settings";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import { resolveSettingsVisibleTab } from "@/components/settings/resolve-settings-visible-tab";

export const Route = createFileRoute("/_authenticated/settings")({
  component: Settings,
});

function Settings() {
  const { data: currentUser } = useCurrentUser();
  const ability = useAbility();
  const isSuperAdmin =
    currentUser?.role === UserRole.SUPERADMIN || ability.can("manage", "all");
  const showDevSettings = isSuperAdmin;
  const phiOnlyDevSettings = !devOverridesEnabled;
  const [selectedTab, setSelectedTab] = useState(
    isSuperAdmin ? "ai" : "schedule",
  );
  const { data: settings, isLoading, isError } = useFetchSettings(isSuperAdmin);
  useTrackPage(PostHogEvents.PAGE_SETTINGS);

  const promptSettings = settings?.find(
    (setting) => setting.name === "AI_PROMPT",
  )?.value as PromptSettingsSchema;

  const reactivationSettings = settings?.find(
    (setting) => setting.name === "REACTIVATION_PROMPT",
  )?.value as ReactivationSettingsSchema;

  const canReadGroups = ability.can("read", "groups");
  const canReadTrainingTeenContent = ability.can(
    "read",
    "training-simulator-content",
  );
  const mentorProfileEnabled = useMentorProfileEnabled();
  const userRecordsV2 = useUserRecordsV2();
  const isMentor = isMentorIdentity(currentUser?.systemRoles);

  const hideMentorSelfServeTabs = userRecordsV2 && isMentor;

  const visibility = {
    isSuperAdmin,
    showSchedule: !hideMentorSelfServeTabs,
    showMatchingProfile:
      mentorProfileEnabled && isMentor && !hideMentorSelfServeTabs,
    canReadTrainingTeenContent,
    canReadGroups,
    showDevSettings,
  };

  const visibleTab = resolveSettingsVisibleTab(selectedTab, visibility);

  if (!visibleTab) {
    if (userRecordsV2 && isMentor) {
      return <Navigate to="/profile/account" />;
    }
    return null;
  }

  return (
    <div className="container max-w-6xl bg-white py-4">
      <div className="my-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold leading-7 text-black">Settings</h1>
        {isSuperAdmin ? <UserRecordsV2Switch /> : null}
      </div>

      <Tabs
        defaultValue={isSuperAdmin ? "ai" : "schedule"}
        value={visibleTab}
        onValueChange={setSelectedTab}
      >
        <TabsList>
          {isSuperAdmin && <TabsTrigger value="ai">AI Settings</TabsTrigger>}
          {isSuperAdmin && (
            <TabsTrigger value="reactivation">Reactivation</TabsTrigger>
          )}
          {visibility.showSchedule && (
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
          )}
          {visibility.showMatchingProfile && (
            <TabsTrigger value="matching-profile">Profile</TabsTrigger>
          )}
          {canReadTrainingTeenContent && (
            <TabsTrigger value="training-teens">Training Teens</TabsTrigger>
          )}
          {canReadGroups && <TabsTrigger value="groups">Groups</TabsTrigger>}
          {showDevSettings && <TabsTrigger value="dev">Dev</TabsTrigger>}
        </TabsList>
        {isSuperAdmin && (
          <TabsContent value="ai">
            {visibleTab === "ai" && (
              <>
                {isLoading && (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                {isError && (
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-700">
                      Failed to load AI settings
                    </p>
                  </div>
                )}
                {!isLoading && !isError && (
                  <PromptSettings defaultValues={promptSettings} />
                )}
              </>
            )}
          </TabsContent>
        )}
        {isSuperAdmin && (
          <TabsContent value="reactivation">
            {visibleTab === "reactivation" && (
              <>
                {isLoading && (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                {isError && (
                  <div className="rounded-md bg-red-50 p-4">
                    <p className="text-sm text-red-700">
                      Failed to load reactivation settings
                    </p>
                  </div>
                )}
                {!isLoading && !isError && (
                  <ReactivationSettings defaultValues={reactivationSettings} />
                )}
              </>
            )}
          </TabsContent>
        )}
        {visibility.showSchedule && (
          <TabsContent value="schedule">
            {visibleTab === "schedule" && <ScheduleSettings />}
          </TabsContent>
        )}
        {visibility.showMatchingProfile && (
          <TabsContent value="matching-profile">
            {visibleTab === "matching-profile" && <MatchingProfileSettings />}
          </TabsContent>
        )}
        {canReadTrainingTeenContent && (
          <TabsContent value="training-teens">
            {visibleTab === "training-teens" && <TrainingTeenSettings />}
          </TabsContent>
        )}
        {canReadGroups && (
          <TabsContent value="groups">
            {visibleTab === "groups" && <GroupSettings />}
          </TabsContent>
        )}
        {showDevSettings && (
          <TabsContent value="dev">
            {visibleTab === "dev" && (
              <DevSettings phiOnly={phiOnlyDevSettings} />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
